import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Edit2, Trash2, Star, Tags } from 'lucide-react';
import { fetchWithAuth, API_URL } from '../utils/api';
import { useLanguage } from './LanguageContext';
import { generateDrugLabelHTML } from '../utils/drugLabelHtml';

const BLANK = {
  label_name: '', preset_id: '', template_id: '',
  label_width_mm: '63.5', label_height_mm: '38.1',
  font_size: '10', font_family: 'Sarabun',
  margin_top: '2', margin_bottom: '2', margin_left: '2', margin_right: '2',
  include_logo: false, is_default: false,
};

// Realistic sample data, so the preview exercises every element a real label
// has — including the three-valued food timing and a drug allergy.
//
// The dosage block carries the SAME shape a real print-label payload does
// (spec §5.3): the stored code plus the en/th labels the API joins from
// medication_frequencies / medication_routes. That matters more than it looks
// — the preview is the only place an admin can tell a working label from a
// broken one, so if it showed a raw "tid" where a real label shows
// "วันละ 3 ครั้ง" (or the reverse) the screen would be lying about the thing
// it exists to demonstrate. Values below are the live `tid` and `oral` rows.
const SAMPLE = {
  patient: { dn: 'DN-69-00001', name: 'สมชาย ใจดี', name_en: 'Somchai Jaidee', drug_allergy: 'Penicillin' },
  medication: { name: 'Amoxicillin', strength: '500 mg', unit: 'cap' },
  dosage: {
    quantity: 15, dose_per_time: '1', duration_days: 5,
    frequency: 'tid',
    frequency_label_en: 'Three times daily', frequency_label_th: 'วันละ 3 ครั้ง',
    route: 'oral',
    route_label_en: 'Oral', route_label_th: 'รับประทาน',
    before_food: 0, after_food: 2, meal_times: 'morning,noon,evening',
    special_instructions: 'Finish the full course',
  },
  prescription: { number: 'RX-260900001', date: new Date().toISOString().slice(0, 10) },
};

const DrugLabelSettings = ({ currentBranch }) => {
  const { print_lang, currentLanguage, translations } = useLanguage();
  const [settings, setSettings] = useState([]);
  const [presets, setPresets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(BLANK);
  const [formError, setFormError] = useState('');
  // The row awaiting an in-page delete confirmation — no native window.confirm.
  const [confirmDelete, setConfirmDelete] = useState(null);

  const branchId = currentBranch?.branch_id;

  const load = useCallback(async () => {
    if (!branchId) return;
    try {
      const res = await fetchWithAuth(`/api/labels/settings?branch_id=${branchId}`);
      setSettings(res?.data || []);
    } catch { setSettings([]); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchWithAuth('/api/labels/size-presets').then((r) => setPresets(r?.data || [])).catch(() => {});
    fetchWithAuth('/api/labels/templates').then((r) => setTemplates(r?.data || [])).catch(() => {});
  }, []);

  const openAdd = () => { setSelected(null); setFormData(BLANK); setFormError(''); setShowModal(true); };
  const openEdit = (s) => {
    setSelected(s); setFormError('');
    setFormData({
      label_name: s.label_name || '', preset_id: s.preset_id ?? '', template_id: s.template_id ?? '',
      label_width_mm: s.label_width_mm ?? '63.5', label_height_mm: s.label_height_mm ?? '38.1',
      font_size: s.font_size ?? '10', font_family: s.font_family || 'Sarabun',
      margin_top: s.margin_top ?? '2', margin_bottom: s.margin_bottom ?? '2',
      margin_left: s.margin_left ?? '2', margin_right: s.margin_right ?? '2',
      include_logo: !!s.include_logo, is_default: !!s.is_default,
    });
    setShowModal(true);
  };

  // Choosing a stock size fills the dimensions; they stay editable for custom media.
  const choosePreset = (presetId) => {
    const p = presets.find((x) => String(x.preset_id) === String(presetId));
    setFormData((f) => ({
      ...f, preset_id: presetId,
      ...(p && { label_width_mm: p.width_mm, label_height_mm: p.height_mm }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    // HTML5 `required` accepts a whitespace-only value, and the API's own
    // rejection of that has no `code` — pre-validate here so the Thai
    // operator sees a translated message instead of the raw English one
    // (same convention as Medications.js / Shop.js).
    if (!formData.label_name.trim()) {
      setFormError(print_lang('label_name_required', 'Label name is required'));
      return;
    }
    try {
      const body = { ...formData, branch_id: branchId };
      if (selected) {
        await fetchWithAuth(`/api/labels/settings/${selected.setting_id}`, {
          method: 'PUT', body: JSON.stringify(body),
        });
      } else {
        await fetchWithAuth('/api/labels/settings', { method: 'POST', body: JSON.stringify(body) });
      }
      setShowModal(false);
      load();
    } catch (err) {
      const byCode = {
        LABEL_WIDTH_INVALID: print_lang('label_width_invalid', 'Width must be between 1 and 300 mm'),
        LABEL_HEIGHT_INVALID: print_lang('label_height_invalid', 'Height must be between 1 and 300 mm'),
        FONT_SIZE_INVALID: print_lang('font_size_invalid', 'Font size must be between 5 and 40'),
        MARGIN_INVALID: print_lang('margin_invalid', 'Margins must be between 0 and 50 mm'),
        MARGINS_EXCEED_LABEL: print_lang('margins_exceed_label', 'Margins leave no room to print'),
      };
      setFormError(byCode[err.code] || err.message || print_lang('save_failed', 'Save failed'));
    }
  };

  const makeDefault = async (s) => {
    setFormError('');
    try {
      await fetchWithAuth(`/api/labels/settings/${s.setting_id}/default`, { method: 'PUT' });
      load();
    } catch (err) {
      // A 404 means the row was soft-deleted from under us, and a 409 means
      // another request already changed the default — either way, re-sync
      // the list rather than leaving a stale row on screen. No native
      // dialog: the same inline banner the form modal uses.
      setFormError(err.message || print_lang('save_failed', 'Save failed'));
      load();
    }
  };

  // Delete is a two-step, in-page confirmation — no window.confirm.
  const requestDelete = (s) => { setFormError(''); setConfirmDelete(s); };

  const confirmRemove = async () => {
    const s = confirmDelete;
    setConfirmDelete(null);
    if (!s) return;
    try {
      await fetchWithAuth(`/api/labels/settings/${s.setting_id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setFormError(err.message || print_lang('delete_failed', 'Delete failed'));
      load();
    }
  };

  const templateCode = useMemo(() => {
    const t = templates.find((x) => String(x.template_id) === String(formData.template_id));
    return t ? t.code : 'A';
  }, [templates, formData.template_id]);

  // The preview and the printer run the SAME generator, so a difference here
  // would be a difference on the roll. branch.language is passed through
  // deliberately: the label renders in the branch's language, not whatever
  // the operator's UI toggle happens to be set to.
  const previewHtml = useMemo(() => generateDrugLabelHTML({
    settings: { ...formData, template: templateCode },
    ...SAMPLE,
    branch: {
      name: currentBranch?.name, phone: currentBranch?.phone,
      mobile_phone: currentBranch?.mobile_phone, logo_path: currentBranch?.logo_path,
      language: currentBranch?.language,
    },
    copies: 1,
  }, { print_lang, currentLanguage, translations, apiUrl: API_URL, autoPrint: false }),
  [formData, templateCode, currentBranch, print_lang, currentLanguage, translations]);

  // generateDrugLabelHTML shrinks the base font deterministically (in fixed
  // steps, down to a legibility floor) until the sample content fits the
  // configured box, and exposes the outcome as data attributes on the
  // returned document's <html> tag — data-label-overflow="true" means it
  // still didn't fit even at the floor. Read straight off the HTML string
  // rather than the iframe's contentDocument, so this doesn't depend on the
  // iframe having finished loading.
  const previewMeta = useMemo(() => {
    const overflow = /data-label-overflow="true"/.test(previewHtml);
    const fsMatch = previewHtml.match(/data-label-font-size="([\d.]+)"/);
    return { overflow, effectiveFontSize: fsMatch ? Number(fsMatch[1]) : Number(formData.font_size) };
  }, [previewHtml, formData.font_size]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Tags size={22} /> {print_lang('label_settings', 'Drug Label Settings')}
        </h1>
        <button className="btn btn-primary flex items-center gap-2" onClick={openAdd}>
          <Plus size={18} /> {print_lang('add_label_setting', 'Add Label')}
        </button>
      </div>

      {/* List-level errors (set default, delete) surface here — the modal
          isn't open when these actions run, so they can't use its banner. */}
      {!showModal && formError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
          {formError}
        </div>
      )}

      {settings.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {print_lang('no_label_settings', 'No label settings yet')}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{print_lang('label_name', 'Label Name')}</th>
              <th>{print_lang('label_size', 'Label Size')}</th>
              <th>{print_lang('label_template', 'Layout')}</th>
              <th>{print_lang('font_size', 'Font Size')}</th>
              <th>{print_lang('set_as_default', 'Default label')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {settings.map((s) => (
              <tr key={s.setting_id}>
                <td className="font-medium">{s.label_name}</td>
                <td>{Number(s.label_width_mm)} × {Number(s.label_height_mm)} mm</td>
                <td>{s.template_name || '-'}</td>
                <td>{s.font_size}</td>
                <td>
                  {s.is_default ? (
                    <span className="badge badge-success inline-flex items-center gap-1">
                      <Star size={12} /> {print_lang('set_as_default', 'Default label')}
                    </span>
                  ) : (
                    <button className="text-sm text-blue-600" onClick={() => makeDefault(s)}>
                      {print_lang('set_as_default', 'Default label')}
                    </button>
                  )}
                </td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <button className="p-2 hover:bg-gray-100 rounded" onClick={() => openEdit(s)}>
                      <Edit2 size={16} className="text-blue-600" />
                    </button>
                    <button className="p-2 hover:bg-red-50 rounded" onClick={() => requestDelete(s)}>
                      <Trash2 size={16} className="text-red-600" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowModal(false);
        }}>
          <div className="modal-content w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {selected ? print_lang('edit', 'Edit') : print_lang('add_label_setting', 'Add Label')}
            </h2>

            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-3 gap-6">
              <form onSubmit={handleSubmit} className="col-span-2">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('label_name', 'Label Name')} *
                    </label>
                    <input type="text" className="w-full border rounded px-3 py-2" required
                           value={formData.label_name}
                           onChange={(e) => setFormData({ ...formData, label_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('label_template', 'Layout')}
                    </label>
                    <select className="w-full border rounded px-3 py-2" value={formData.template_id}
                            onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}>
                      <option value="">-</option>
                      {templates.map((t) => (
                        <option key={t.template_id} value={t.template_id}>
                          {t.code} — {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('label_size', 'Label Size')}
                    </label>
                    <select className="w-full border rounded px-3 py-2" value={formData.preset_id}
                            onChange={(e) => choosePreset(e.target.value)}>
                      <option value="">{print_lang('custom_size', 'Custom size')}</option>
                      {presets.map((p) => (
                        <option key={p.preset_id} value={p.preset_id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('width_mm', 'Width (mm)')}
                    </label>
                    <input type="number" step="0.1" min="1" max="300"
                           className="w-full border rounded px-3 py-2" value={formData.label_width_mm}
                           onChange={(e) => setFormData({ ...formData, label_width_mm: e.target.value, preset_id: '' })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('height_mm', 'Height (mm)')}
                    </label>
                    <input type="number" step="0.1" min="1" max="300"
                           className="w-full border rounded px-3 py-2" value={formData.label_height_mm}
                           onChange={(e) => setFormData({ ...formData, label_height_mm: e.target.value, preset_id: '' })} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('font_size', 'Font Size')}
                    </label>
                    <input type="number" min="5" max="40" className="w-full border rounded px-3 py-2"
                           value={formData.font_size}
                           onChange={(e) => setFormData({ ...formData, font_size: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('font_family', 'Font')}
                    </label>
                    <select className="w-full border rounded px-3 py-2" value={formData.font_family}
                            onChange={(e) => setFormData({ ...formData, font_family: e.target.value })}>
                      {/* Sarabun is the Thai face the rest of the printed
                          documents use; the others are safe fallbacks. */}
                      <option value="Sarabun">Sarabun</option>
                      <option value="Arial">Arial</option>
                      <option value="Tahoma">Tahoma</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('margins_mm', 'Margins (mm)')}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {['margin_top', 'margin_right', 'margin_bottom', 'margin_left'].map((m) => (
                      <input key={m} type="number" step="0.5" min="0" max="50"
                             className="w-full border rounded px-2 py-1" value={formData[m]}
                             placeholder={m.replace('margin_', '')}
                             onChange={(e) => setFormData({ ...formData, [m]: e.target.value })} />
                    ))}
                  </div>
                </div>

                {/* include_logo only. `include_qr_code` and `include_barcode`
                    used to sit here too: they persisted and were forwarded to
                    the generator, which never drew either. Ticking one changed
                    nothing in the true-scale preview beside it, and the natural
                    reading of that is "the preview is wrong, the printer will
                    do it" — which is an attack on the single property this
                    screen exists to guarantee. Better absent than present and
                    inert. The columns remain in drug_label_settings so a later
                    plan can implement rendering without a migration. */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
                  {/* Each label is a flex ITEM here, so it already sizes to its
                      own content — only the box and its own text are clickable,
                      not the whole row. Nothing here relies on a width class:
                      this app pins Tailwind 2.2, where the obvious one for the
                      job does not exist and would silently do nothing. */}
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={formData.include_logo}
                           onChange={(e) => setFormData({ ...formData, include_logo: e.target.checked })} />
                    {print_lang('include_logo', 'Include clinic logo')}
                  </label>

                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={formData.is_default}
                           onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })} />
                    {print_lang('set_as_default', 'Default label')}
                  </label>
                </div>

                <div className="flex justify-end gap-2">
                  <button type="button" className="btn" onClick={() => setShowModal(false)}>
                    {print_lang('cancel', 'Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {print_lang('save', 'Save')}
                  </button>
                </div>
              </form>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {print_lang('label_preview', 'Preview')}
                </label>
                <div className="border rounded bg-gray-100 p-3 flex justify-center">
                  {/* At true physical scale: mm in the iframe are mm on the roll. */}
                  <iframe
                    title="label-preview"
                    srcDoc={previewHtml}
                    style={{
                      width: `${formData.label_width_mm}mm`,
                      height: `${formData.label_height_mm}mm`,
                      border: '1px solid #999',
                      background: '#fff',
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {Number(formData.label_width_mm)} × {Number(formData.label_height_mm)} mm
                </p>
                {/* The shrink hint below is yellow-, not amber-: this app pins
                    Tailwind 2.2, whose default palette carries no amber at all,
                    so text-amber-600 was rendering the warning in plain body
                    colour. v2's yellow-600 is #d97706 — the exact hue amber-600
                    means, so this restores the intent rather than changing it. */}
                {previewMeta.overflow ? (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-2">
                    {print_lang('label_overflow_warning',
                      "Content doesn't fit even at the smallest legible size. Choose a larger label or shorten the text.")}
                  </div>
                ) : previewMeta.effectiveFontSize < Number(formData.font_size) && (
                  <p className="text-xs text-yellow-600 mt-2">
                    {print_lang('label_auto_shrunk', 'Font auto-shrunk to fit')}: {previewMeta.effectiveFontSize}px
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-page delete confirmation — no window.confirm anywhere in this
          component. A click outside the box cancels, same as the add/edit
          modal above. */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setConfirmDelete(null);
        }}>
          <div className="modal-content w-full max-w-sm p-6">
            <h3 className="text-lg font-bold mb-3">
              {print_lang('confirm_delete', 'Delete this item?')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">{confirmDelete.label_name}</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setConfirmDelete(null)}>
                {print_lang('cancel', 'Cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmRemove}>
                {print_lang('delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrugLabelSettings;
